// Lightweight imperative bridge to the global Flow chat (FlowChatLauncher).
//
// The launcher lives in the Layout shell, outside the routed page tree, so a
// page (e.g. the dashboard grid's "fix this widget" shortcut) can't reach its
// open/send handlers through props. The launcher registers a handler on mount;
// any page calls flowChatBus.open(prompt?) to open the chat and optionally send
// an opening message into the current Flow conversation.

type FlowChatOpenHandler = (prompt?: string) => void

let handler: FlowChatOpenHandler | null = null
let closeHandler: (() => void) | null = null
/** The athlete the registered launcher's chat is settled for. */
let handlerProfileId: number | null | undefined
/** A prompt for one athlete, waiting for a launcher settled on that athlete. */
let queued: { profileId: number | null; prompt: string; at: number } | null = null
// A queued prompt that no launcher picks up soon (the athlete switch failed,
// or the page has no launcher) is dropped rather than surfacing later on
// some other page.
const QUEUE_TTL_MS = 10_000
const sentListeners = new Set<() => void>()

function deliverQueued(): void {
  if (!queued || !handler || handlerProfileId !== queued.profileId) return
  const { prompt, at } = queued
  queued = null
  if (Date.now() - at <= QUEUE_TTL_MS) handler(prompt)
}

export const flowChatBus = {
  /** Called by FlowChatLauncher on mount; returns an unregister cleanup.
   *  `settledProfileId` is the athlete its chat is bound to, once settled
   *  (undefined while a switch is still resetting it). */
  register(next: FlowChatOpenHandler, onClose?: () => void, settledProfileId?: number | null): () => void {
    handler = next
    closeHandler = onClose ?? null
    handlerProfileId = settledProfileId
    deliverQueued()
    return () => {
      if (handler === next) {
        handler = null
        closeHandler = null
        handlerProfileId = undefined
      }
    }
  },
  /** Open Flow in the chat about `profileId` and send `prompt` there. If the
   *  launcher is not settled on that athlete yet (a page just selected them),
   *  the prompt waits for it, so it can never land in the previous athlete's
   *  conversation. */
  openFor(profileId: number | null, prompt: string): void {
    queued = { profileId, prompt, at: Date.now() }
    deliverQueued()
  },
  /** Open the global Flow chat, optionally sending an opening prompt. */
  open(prompt?: string): void {
    handler?.(prompt)
  },
  /** Minimize it back to the launcher pill (e.g. while a guided tour runs). */
  close(): void {
    closeHandler?.()
  },
  /** Called by FlowChatLauncher whenever a message goes out, typed, chip or prompt. */
  notifySent(): void {
    sentListeners.forEach((listener) => listener())
  },
  /** Be told when the coach sends a message (a guided tour waits on it). */
  onSent(listener: () => void): () => void {
    sentListeners.add(listener)
    return () => { sentListeners.delete(listener) }
  },
}
